import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '../constants';
import type { ApiResponse } from '@/types';
import { buildListQuery, type ListParams, type Paginated } from './agentService';

export interface Ecosystem {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  state: 'draft' | 'published' | 'archived';
  config?: Record<string, any>;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  entryAgentMappings?: EcosystemEntryAgentMapping[];
  functionalAgentMappings?: EcosystemFunctionalAgentMapping[];
}

export interface EcosystemEntryAgentMapping {
  id: string;
  ecosystemId: string;
  agentInstanceId: string;
  entryType: 'text' | 'inbound-voice' | 'outbound-voice';
  isActive: boolean;
  config?: Record<string, any>;
  agentInstance?: { id: string; name: string; code: string; agentType?: any };
}

export interface EcosystemFunctionalAgentMapping {
  id: string;
  ecosystemId: string;
  agentInstanceId: string;
  isActive: boolean;
  priority: number;
  agentInstance?: { id: string; name: string; code: string; agentType?: any };
}

export const ecosystemService = {
  async list(params?: ListParams): Promise<Paginated<Ecosystem>> {
    const query = buildListQuery(params);
    const url = query ? `${API_ENDPOINTS.ECOSYSTEMS.LIST}?${query}` : API_ENDPOINTS.ECOSYSTEMS.LIST;
    const result = await apiClient.get<ApiResponse<Paginated<Ecosystem>>>(url, { authenticate: true });
    return (result as any).data ?? result;
  },

  async getById(id: string): Promise<Ecosystem> {
    const result = await apiClient.get<ApiResponse<Ecosystem>>(
      API_ENDPOINTS.ECOSYSTEMS.GET_BY_ID(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async create(payload: Partial<Ecosystem>): Promise<Ecosystem> {
    const result = await apiClient.post<ApiResponse<Ecosystem>>(
      API_ENDPOINTS.ECOSYSTEMS.CREATE,
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async update(id: string, payload: Partial<Ecosystem>): Promise<Ecosystem> {
    const result = await apiClient.put<ApiResponse<Ecosystem>>(
      API_ENDPOINTS.ECOSYSTEMS.UPDATE(encodeURIComponent(id)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async delete(id: string): Promise<{ success: boolean }> {
    const result = await apiClient.delete<ApiResponse<{ success: boolean }>>(
      API_ENDPOINTS.ECOSYSTEMS.DELETE(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async addEntryAgent(id: string, payload: { agentInstanceId: string; entryType: string; isActive?: boolean; config?: any }): Promise<any> {
    const result = await apiClient.post<ApiResponse<any>>(
      API_ENDPOINTS.ECOSYSTEMS.ENTRY_AGENTS.ADD(encodeURIComponent(id)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async updateEntryAgentConfig(ecosystemId: string, agentInstanceId: string, payload: { twilioPhoneNumber?: string; config?: Record<string, any> }): Promise<any> {
    const result = await apiClient.patch<any>(
      API_ENDPOINTS.ECOSYSTEMS.ENTRY_AGENTS.UPDATE_CONFIG(encodeURIComponent(ecosystemId), encodeURIComponent(agentInstanceId)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async removeEntryAgent(id: string, agentInstanceId: string): Promise<any> {
    const result = await apiClient.delete<ApiResponse<any>>(
      API_ENDPOINTS.ECOSYSTEMS.ENTRY_AGENTS.REMOVE(encodeURIComponent(id), encodeURIComponent(agentInstanceId)),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async addFunctionalAgent(id: string, payload: { agentInstanceId: string; isActive?: boolean; priority?: number }): Promise<any> {
    const result = await apiClient.post<ApiResponse<any>>(
      API_ENDPOINTS.ECOSYSTEMS.FUNCTIONAL_AGENTS.ADD(encodeURIComponent(id)),
      { agentInstanceIds: [payload.agentInstanceId], priority: payload.priority ?? 0 },
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async removeFunctionalAgent(id: string, agentInstanceId: string): Promise<any> {
    const result = await apiClient.delete<ApiResponse<any>>(
      API_ENDPOINTS.ECOSYSTEMS.FUNCTIONAL_AGENTS.REMOVE(encodeURIComponent(id), encodeURIComponent(agentInstanceId)),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async sendMessage(payload: {
    ecosystemId: string;
    userId: string;
    message: string;
    conversationId?: string;
  }): Promise<any> {
    const result = await apiClient.post<any>(
      API_ENDPOINTS.ECOSYSTEMS.CHAT,
      payload,
      { authenticate: true }
    );
    return result;
  },

  async sendMessageStream(payload: {
    ecosystemId: string;
    userId: string;
    message: string;
    conversationId?: string;
    userTimezone?: string;
  }): Promise<Response> {
    const { response } = await apiClient.postStream(
      API_ENDPOINTS.ECOSYSTEMS.CHAT,
      payload,
      { authenticate: true, timeoutMs: 120000 }
    );
    return response;
  },

  async listConversations(ecosystemId: string, params?: { page?: number; limit?: number }): Promise<{ records: any[]; total: number }> {
    const qs = new URLSearchParams();
    qs.set('ecosystemId', ecosystemId);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const result = await apiClient.get<ApiResponse<any>>(
      `${API_ENDPOINTS.ECOSYSTEMS.CONVERSATIONS}?${qs.toString()}`,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async getConversationById(conversationId: string): Promise<any> {
    const result = await apiClient.get<ApiResponse<any>>(
      API_ENDPOINTS.ECOSYSTEMS.CONVERSATION_BY_ID(encodeURIComponent(conversationId)),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },
};
